import { useState, useEffect, createContext, useContext, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isStarviePlayer: boolean;
  loading: boolean;
  adminCheckError: string | null;
}

const AuthContext = createContext<AuthContextType>({ user: null, isAdmin: false, isStarviePlayer: false, loading: true, adminCheckError: null });

type RoleCheckResult =
  | { ok: true; isAdmin: boolean; isStarviePlayer: boolean }
  | { ok: false; error: unknown };

async function checkRoles(userId: string): Promise<RoleCheckResult> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 8000);
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .in("role", ["admin", "starvie_player"] as any)
        .abortSignal(ctrl.signal);
      if (error) throw error;
      const roles = ((data ?? []) as Array<{ role: string }>).map((r) => r.role);
      return { ok: true, isAdmin: roles.includes("admin"), isStarviePlayer: roles.includes("starvie_player") };
    } finally {
      clearTimeout(t);
    }
  } catch (error) {
    return { ok: false, error };
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStarviePlayer, setIsStarviePlayer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminCheckError, setAdminCheckError] = useState<string | null>(null);
  const initialised = useRef(false);
  const adminResolvedFor = useRef<string | null>(null);
  const adminCheckSeq = useRef(0);

  useEffect(() => {
    let mounted = true;

    // Helper: resolve admin status without blocking the auth callback.
    // CRITICAL: never await inside onAuthStateChange — that deadlocks the
    // Supabase auth client and freezes all subsequent queries.
    const resolveAdmin = (uid: string | null, options?: { force?: boolean }) => {
      if (!uid) {
        adminResolvedFor.current = null;
        adminCheckSeq.current += 1;
        if (mounted) {
          setIsAdmin(false);
          setIsStarviePlayer(false);
          setAdminCheckError(null);
          setLoading(false);
          initialised.current = true;
        }
        return;
      }
      // Skip duplicate work (e.g. TOKEN_REFRESHED for the same resolved user)
      if (!options?.force && adminResolvedFor.current === uid) {
        if (mounted) setLoading(false);
        initialised.current = true;
        return;
      }
      const alreadyResolvedForUser = adminResolvedFor.current === uid;
      const seq = ++adminCheckSeq.current;
      if (mounted) {
        setAdminCheckError(null);
        if (!alreadyResolvedForUser) {
          setLoading(true);
          if (adminResolvedFor.current !== uid) {
            setIsAdmin(false);
            setIsStarviePlayer(false);
          }
        }
      }
      checkRoles(uid).then((result) => {
        if (!mounted) return;
        if (seq === adminCheckSeq.current) {
          if (result.ok === true) {
            adminResolvedFor.current = uid;
            setIsAdmin(result.isAdmin);
            setIsStarviePlayer(result.isStarviePlayer);
            setAdminCheckError(null);
          } else {
            console.error("Role check failed", result.error);
            setAdminCheckError("Admin access check failed. Retrying shortly…");
            window.setTimeout(() => {
              if (mounted && seq === adminCheckSeq.current) {
                resolveAdmin(uid, { force: true });
              }
            }, 1500);
          }
          setLoading(false);
          initialised.current = true;
        }
      }).catch(() => {
        if (mounted) {
          setAdminCheckError("Admin access check failed. Retrying shortly…");
          setLoading(false);
          initialised.current = true;
        }
      });
    };

    // 1. Subscribe FIRST so we never miss an event.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Some clients emit INITIAL_SESSION with null before storage restore is complete.
      // The explicit getSession() below is the authority for first load.
      if (event === "INITIAL_SESSION") return;

      const currentUser = session?.user ?? null;
      if (mounted) setUser(currentUser);
      // Token refresh keeps the same user — don't re-run admin check.
      if (event === "TOKEN_REFRESHED" && currentUser && adminResolvedFor.current === currentUser.id) {
        return;
      }
      resolveAdmin(currentUser?.id ?? null, { force: event === "SIGNED_IN" });
    });

    // 2. Then load the initial session.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      resolveAdmin(currentUser?.id ?? null, { force: true });
    }).catch(() => {
      if (!mounted) return;
      setAdminCheckError("Auth session fetch failed. Retrying shortly…");
      setLoading(false);
      initialised.current = true;
    });

    // 3. Safety timeout — never stay loading forever
    const timeout = setTimeout(() => {
      if (!initialised.current && mounted) {
        console.error("Auth session fetch timed out");
        setAdminCheckError("Auth session fetch timed out. Retrying shortly…");
        setLoading(false);
        initialised.current = true;
      }
    }, 4000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  if (loading) {
    return (
      <AuthContext.Provider value={{ user, isAdmin, isStarviePlayer, loading: true, adminCheckError }}>
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={{ user, isAdmin, isStarviePlayer, loading, adminCheckError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
